import React, {
  ReactPortal,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import * as cookie from "cookie";
import { useQuery } from "@apollo/client";

import { ExperimentDebugger } from "./ExperimentDebugger";
import { FeatureFlagsDocument } from "./schema";

const cookieKey = "_otta_experiment_debugger";
const localStorageKey = "_otta_experiment_debugger_state";

export function useExperimentDebugger(): [
  ReactPortal | null,
  Record<string, string>,
  (name: string, variant: string) => void | (() => void)
] {
  const [lastSeenVariants, setLastSeenVariants] = useState<
    Record<string, string>
  >({});

  const [activeExperiments, setActiveExperiments] = useState<
    Record<string, number>
  >({});

  const [variantOverrides, setVariantOverrides] = useState<
    Record<string, string>
  >({});

  const [portal, setPortal] = useState<HTMLDivElement | null>(null);

  const { data } = useQuery(FeatureFlagsDocument, {
    errorPolicy: "all",
    skip: !portal,
  });

  useEffect(() => {
    const cookies = cookie.parse(document.cookie);

    if (cookies[cookieKey]) {
      const el = document.createElement("div");
      el.id = "experiment-debugger";
      document.body.appendChild(el);
      setPortal(el);
    }

    const item = window.localStorage.getItem(localStorageKey);

    if (item) {
      const storedValue = JSON.parse(item);
      setVariantOverrides(storedValue);
    }
  }, []);

  const handleOverrideChange = useCallback(
    (name: string, variant: string) => {
      const newOverrides = { ...variantOverrides, [name]: variant };

      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify(newOverrides)
      );

      setVariantOverrides(newOverrides);
    },
    [variantOverrides]
  );

  const handleVariantSeen = useCallback((name: string, variant: string) => {
    setLastSeenVariants(v => ({ ...v, [name]: variant }));

    setActiveExperiments(v => ({ ...v, [name]: (v[name] ?? 0) + 1 }));

    return () => {
      setActiveExperiments(v => ({
        ...v,
        [name]: (v[name] ?? 1) - 1,
      }));
    };
  }, []);

  const mergedExperiments = useMemo(
    () =>
      data?.featureFlags.reduce<Record<string, string[]>>((acc, flag) => {
        if (flag.variants.length) {
          acc[flag.name] = ["control", ...flag.variants];
        } else {
          acc[flag.name] = ["control", "variant"];
        }

        return acc;
      }, {}) ?? {},
    [data?.featureFlags]
  );

  const activeVariants = useMemo(
    () =>
      Object.keys(activeExperiments)
        .filter(name => activeExperiments[name] > 0)
        .reduce<Record<string, string>>((acc, name) => {
          acc[name] = lastSeenVariants[name];
          return acc;
        }, {}),
    [activeExperiments, lastSeenVariants]
  );

  return [
    portal &&
      createPortal(
        <ExperimentDebugger
          onChange={handleOverrideChange}
          experiments={mergedExperiments}
          activeVariants={activeVariants}
        />,
        portal
      ),
    variantOverrides,
    handleVariantSeen,
  ];
}
